import React, { useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SAINResearchStep } from '../../../../ai-answer-dialog/types';

import { ShimmerText } from './shimmer-text';

const styles = cssMap({
	stepRow: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		columnGap: token('space.100'),
		alignItems: 'stretch',
		position: 'relative',
	},
	/**
	 * The icon column is a flex-column that stretches the full row height.
	 * Two "rule slots" (one above, one below the icon) each flex-grow:1 so the
	 * icon naturally centres vertically between the title and content rows.
	 * Whether each slot renders the connector line is controlled by props.
	 */
	iconColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: '24px',
		flexShrink: 0,
		alignSelf: 'stretch',
	},
	/**
	 * Rule slot — always flex-grow:1 so the icon is centred. Renders as a
	 * vertical line when the `withLine` flag is set; otherwise it's an
	 * invisible spacer that still centres the icon.
	 */
	ruleSlot: {
		flexGrow: 1,
		width: '0px',
	},
	ruleSlotWithLine: {
		flexGrow: 1,
		width: '0px',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
	},
	stepContent: {
		flexGrow: 1,
		minWidth: '0px',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

type ResearchStepRowProps = {
	step: SAINResearchStep;
	/** Render a connector line ABOVE the icon (connects to previous row). */
	showTopConnector?: boolean;
	/** Render a connector line BELOW the icon (connects to next row). */
	showBottomConnector?: boolean;
	/**
	 * When true, hides the icon column entirely. Used in the collapsed loading
	 * state where the MorphingShapeLoader already acts as the visual indicator.
	 */
	hideIcon?: boolean;
	testId?: string;
};

/**
 * Renders a single research step row. The icon is vertically centered between
 * the title and content text. The icon column uses two flex-grow:1 "rule slots"
 * (above and below the icon); each slot can optionally draw a vertical
 * connector line.
 */
export const ResearchStepRow = ({
	step,
	showTopConnector = false,
	showBottomConnector = false,
	hideIcon = false,
	testId,
}: ResearchStepRowProps): React.JSX.Element => {
	const [iconError, setIconError] = useState(false);

	return (
		<Box xcss={styles.stepRow} testId={testId}>
			{!hideIcon && (
				<Box xcss={styles.iconColumn}>
					<Box xcss={showTopConnector ? styles.ruleSlotWithLine : styles.ruleSlot} />
					{step.toolIcon && !iconError ? (
						<img
							src={step.toolIcon}
							alt=""
							width={16}
							height={16}
							onError={() => setIconError(true)}
						/>
					) : null}
					<Box xcss={showBottomConnector ? styles.ruleSlotWithLine : styles.ruleSlot} />
				</Box>
			)}
			<Box xcss={styles.stepContent}>
				<Stack space="space.025">
					{step.title ? (
						step.completed ? (
							<Text weight="medium" color="color.text.subtlest">{step.title}</Text>
						) : (
							<ShimmerText size="medium">{step.title}</ShimmerText>
						)
					) : null}
					{step.separatedContent && step.separatedContent.length > 0 ? (
						<Text color="color.text.subtlest">
							{step.separatedContent[step.separatedContent.length - 1].trim()}
						</Text>
					) : null}
				</Stack>
			</Box>
		</Box>
	);
};
